import { BookingStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { getCarDisplayName } from 'noddi-util';
import { getLongAddressName, useAuthContext } from 'noddi-web';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import {
  useServiceCenter,
  useStationaryAddonServices,
  useStationaryBookingActions,
  useStationaryBookingCar,
  useStationaryBookingSelectedOrganization,
  useStationaryPrimaryService,
  useStationaryReservation
} from '../stores/StationaryBookingStore';
import { deriveStationaryPhoneValidity } from '../utils/stationaryCheckout';
import { toStationarySummaryLineItems } from '../utils/stationarySummaryLineItems';
import { isUserGroupMember } from '../utils/userGroupMembership';
import { useOrganizationCheckout } from './useOrganizationCheckout';
import { useStationaryBookableServices } from './useStationaryBookableServices';
import { useStationaryOrganizationSelection } from './useStationaryOrganizationSelection';

/** Server data + derived summary for the stationary booking summary step. */
export function useStationarySummaryData() {
  const { isLoggedIn, currentUserGroupId: userGroupId, userData } = useAuthContext();
  const serviceCenter = useServiceCenter();
  const reservation = useStationaryReservation();
  const selectedCar = useStationaryBookingCar();
  const primaryService = useStationaryPrimaryService();
  const addonServices = useStationaryAddonServices();
  const { setSelectedOrganization } = useStationaryBookingActions();
  const { openOrder } = useStationaryBookableServices();
  const selectedOrganization = useStationaryBookingSelectedOrganization();
  const { currentBrand } = useCurrentBrand();

  const { hasValidUserPhone, invalidUserPhoneDisplay, isLandline } = deriveStationaryPhoneValidity(
    userData?.user.phoneNumber
  );

  const serviceOrganizationId = serviceCenter?.serviceOrganizationId;
  const licensePlateNumber = selectedCar?.licensePlate.number;

  const {
    data: organizationsForCarData,
    error: organizationsForCarError,
    isLoading: isOrganizationsLoading,
    isFetching: isOrganizationsFetching,
    refetch: refetchOrganizationsForCar
  } = noddiAsync.useGet({
    type: URLKeys.getOrganizationsForCar,
    input: {
      licensePlateNumber: licensePlateNumber!,
      serviceOrganizationIds: [serviceOrganizationId!],
      userGroupId: userGroupId!
    },
    queryConfig: {
      enabled: isLoggedIn && !!licensePlateNumber && !!serviceOrganizationId && !!userGroupId
    }
  });

  // Staff object-access lists every company that owns the plate. Partner reads
  // that list. A customer booking only selects a company this account belongs
  // to, so one it does not belong to stays on the non-member payment options.
  const organizationsForCar = organizationsForCarData?.filter((organization) =>
    isUserGroupMember(organization.userGroupId, userData?.user.userGroups)
  );

  useStationaryOrganizationSelection({
    organizationsForCar,
    selectedOrganization,
    setSelectedOrganization
  });

  const selectedOrgUserGroupId = selectedOrganization?.userGroupId;
  const paymentUserGroupId = selectedOrgUserGroupId ?? userGroupId;
  const organizationName = selectedOrganization?.organizationName ?? '';
  const registrationNumber = selectedOrganization?.registrationNumber;

  const paymentOptionsEnabled = isLoggedIn && !!paymentUserGroupId && !!serviceOrganizationId;
  const licensePlate = selectedCar?.licensePlate;
  const {
    data: paymentOptionsData,
    error: paymentOptionsError,
    isLoading: isPaymentOptionsLoading,
    isFetching: isPaymentOptionsFetching,
    refetch: refetchPaymentOptions
  } = noddiAsync.useGet({
    type: URLKeys.getPaymentOptions,
    input: {
      userGroupId: paymentUserGroupId!,
      serviceOrganizationId: serviceOrganizationId!,
      serviceDepartmentId: serviceCenter?.serviceDepartmentId,
      licensePlateNumbers: licensePlate ? [licensePlate.number] : undefined,
      licensePlateCountryCodes: licensePlate ? [licensePlate.countryCode] : undefined
    },
    queryConfig: {
      enabled: paymentOptionsEnabled
    }
  });

  // Public location endpoint — summary renders the workshop address before login.
  // Do not use getServiceOrganization here (authenticated; 401 for anonymous).
  const {
    data: serviceOrganization,
    error: serviceOrganizationError,
    isFetching: isServiceOrganizationFetching,
    refetch: refetchServiceOrganization
  } = noddiAsync.useGet({
    type: URLKeys.getServiceOrganizationLocation,
    input: { pk: serviceOrganizationId! },
    queryConfig: {
      enabled: !!serviceOrganizationId
    }
  });

  // Season only scopes the existing-bookings probe — skip for visitors who cannot fetch bookings.
  const { data: currentSeason, error: _currentSeasonError } = noddiAsync.useGet({
    type: URLKeys.getRecallSeasonCurrent,
    input: {},
    queryConfig: { enabled: isLoggedIn && !!licensePlateNumber }
  });

  // The group the customer acts as. Data quality and the existing-booking probe
  // stay on it even when the booking itself goes to a non-member organization.
  const memberUserGroupId = selectedOrgUserGroupId ?? userGroupId ?? null;
  const canFetchExistingBookings = isLoggedIn && !!licensePlateNumber && memberUserGroupId != null && !!currentSeason;

  const { data: existingBookings, error: _existingBookingsError } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId: memberUserGroupId ?? 0,
      excludedStatusIds: [BookingStatus.CANCELLED, BookingStatus.COMPLETED],
      search: licensePlateNumber,
      brandDomains: [currentBrand],
      createdAtGte: currentSeason?.dateFrom,
      createdAtLte: currentSeason?.dateTo,
      pageSize: 10,
      pageIndex: 0
    },
    queryConfig: {
      enabled: canFetchExistingBookings
    }
  });

  const existingBookingForSeason =
    currentSeason &&
    (existingBookings?.results ?? []).find(
      (booking) => booking.date >= currentSeason.dateFrom && booking.date <= currentSeason.dateTo
    );

  const openOrderLines = openOrder?.lines ?? [];
  const openOrdersAmount = openOrderLines.reduce((lineAcc, line) => lineAcc + line.grossAmount.amount, 0);

  const totalPrice =
    (primaryService ? primaryService.grossPrice.amount : 0) +
    addonServices.reduce((acc, addonService) => acc + addonService.grossPrice.amount, 0) +
    openOrdersAmount;

  const lineItems = toStationarySummaryLineItems({
    primaryService,
    addonServices,
    openOrderLines
  });

  const locationAddress = serviceOrganization?.address
    ? {
        displayText: getLongAddressName(serviceOrganization.address),
        googleMapsUrl: serviceOrganization.address.googleMapsUrl
      }
    : undefined;

  const carDisplayName = selectedCar ? getCarDisplayName({ car: selectedCar, showYear: false }) : '';
  const currentUserGroup = userData?.user.userGroups.find((ug) => ug.id === userGroupId);
  const customerName = userData ? `${userData.user.firstName} ${userData.user.lastName}` : undefined;

  const organization = useOrganizationCheckout({
    isLoggedIn,
    memberUserGroupId,
    memberOrganization:
      isLoggedIn && selectedOrganization
        ? { userGroupId: selectedOrganization.userGroupId, name: selectedOrganization.organizationName }
        : null,
    licensePlateNumbers: licensePlateNumber ? [licensePlateNumber] : [],
    paymentOptions: paymentOptionsData,
    fallbackNames: [currentUserGroup?.name, customerName]
  });
  const hasMultipleOrganizations = (organizationsForCar?.length ?? 0) > 1;
  // Org number is only required when invoicing the selected booking organization.
  const requiresOrganizationNumber = organization.isInvoice && !!selectedOrganization && !registrationNumber;

  const isSummaryLoading =
    isLoggedIn &&
    (isOrganizationsLoading || (paymentOptionsEnabled && isPaymentOptionsLoading) || organization.isCarDefaultsLoading);

  const summaryError =
    (isLoggedIn && organizationsForCarError) ||
    (isLoggedIn && paymentOptionsError) ||
    serviceOrganizationError ||
    organization.carDefaultsError ||
    null;
  const retrySummary = () => {
    if (organizationsForCarError) {
      void refetchOrganizationsForCar();
    }
    if (paymentOptionsError) {
      void refetchPaymentOptions();
    }
    if (serviceOrganizationError) {
      void refetchServiceOrganization();
    }
    if (organization.carDefaultsError) {
      void organization.refetchCarDefaults();
    }
  };

  return {
    ...organization,
    isLoggedIn,
    isSummaryLoading,
    summaryError,
    retrySummary,
    isSummaryRetrying:
      isOrganizationsFetching ||
      isPaymentOptionsFetching ||
      isServiceOrganizationFetching ||
      organization.isCarDefaultsFetching,
    serviceCenter,
    reservation,
    selectedCar,
    lineItems,
    totalPrice,
    hasMultipleOrganizations,
    requiresOrganizationNumber,
    organizationsForCar,
    selectedOrganization,
    setSelectedOrganization,
    organizationName,
    registrationNumber,
    serviceOrganization,
    locationAddress,
    carDisplayName,
    existingBookingForSeason,
    hasValidUserPhone,
    isLandline,
    userPhoneNumber: userData?.user.phoneNumber ?? undefined,
    invalidUserPhoneDisplay,
    customerName,
    currentUserGroup,
    memberUserGroupId
  };
}
